/* eslint-disable react/no-unknown-property */
import React from 'react';
import { Icon, type IconName } from '@pypestream/design-system';

import { NodeColumns, tableStore } from '../../../stores/table-store';
import { useBobcatFlag } from '../../../hooks/use-bobcat-flag';
import { ExpandableTextarea } from './expandable-textarea';
import { RoutingIntentsField } from './routing-intents-field';
import {
  FieldPath,
  ParameterInputSectionId,
  PARAMETER_INPUT_SECTIONS,
  detectSections,
  fieldPathsForSection,
  getParameterInputPath,
  inferFieldKind,
  labelForKey,
  mergeOtherKeys,
  otherKeys,
  parseParameterInput,
  serializeParameterInput,
  setParameterInputPath,
  summarizeSection,
} from './parameter-input-sections';

const SECTION_ICON: Record<ParameterInputSectionId, IconName> = {
  prompts: 'chat',
  retrieval: 'database',
  model: 'settings',
  tracing: 'data',
  mcp: 'puzzle',
  contract: 'file-txt',
  routing: 'arrow-right',
  wiring: 'link',
  other: 'grid-menu',
};

type Commit = {
  onFocus: () => void;
  onChange: (raw: string) => void;
  onBlur: () => void;
};

/**
 * Project Bobcat — the Parameter Input field's section drill-down.
 *
 * Breaks the flat JSON blob a Gemini-style action node's Parameter Input
 * carries into named, clickable sections (matching the prototype's own
 * list-then-drill-down shape — see `parameter-input-sections.ts`'s doc
 * comment for why the section NAMES differ from the prototype's).
 *
 * Every field here writes back through the SAME `onChange`/`onBlur` pair
 * `NodePropertyField` already built via `useNodeFieldCommit` for this one
 * `(rowId, parameterInput)` cell — this component never calls
 * `tableStore.writeCellValue` directly. That preserves every invariant that
 * commit path already carries (draft-then-canonicalize-on-blur, the JSON
 * order-map, one undo entry), which is why this file has zero knowledge of
 * `tableStore` at all: it only ever computes a new RAW STRING for the whole
 * blob and hands it to the same three callbacks a plain textarea would.
 *
 * Fields inside a section are GENERIC, not hard-coded per section — see
 * `parameter-input-sections.ts`'s module doc comment for why: the same
 * `parameterInput` shape varies across real nodes (an older
 * `chat_context_setting`/`user_utterance`/`safety_settings` shape and a
 * newer `system_prompt`/`user_prompt`/`tracing`/`mcp` one, confirmed against
 * real live nodes, not just the design prototype's mockup data), so a fixed
 * field list would silently show "Not set" for every field on whichever
 * shape it didn't anticipate. Each present key becomes one field, typed off
 * its OWN value; an array or an object nested more than one level deep falls
 * back to a JSON sub-field rather than guessing further into a shape
 * nothing here has validated — the same safety margin the top-level
 * "Raw JSON" escape hatch gives the whole blob.
 *
 * Deliberately NOT built: the prototype's `{VARIABLE}` token-highlighting
 * prose modal for prompt fields — no prior art for it exists anywhere in
 * this codebase (checked), and it is a sizeable feature on its own.
 */
export const ParameterInputField: React.FC<{
  rowId: string;
  raw: string;
  disabled: boolean;
  commit: Commit;
}> = ({ rowId, raw, disabled, commit }) => {
  const [activeSection, setActiveSection] = React.useState<
    ParameterInputSectionId | 'raw' | null
  >(null);

  // `NodePropertyPanel` keys this field on `columnId` alone (not `rowId` —
  // every field in the panel is keyed that way), so React reuses the same
  // component instance across a row change and this state would otherwise
  // survive it: drilling into e.g. "Model" for one node and then selecting a
  // different one would leave the panel showing that section for the NEW
  // row instead of resetting to the section list.
  React.useEffect(() => {
    setActiveSection(null);
  }, [rowId]);

  const manageIntentsRoutingEnabled =
    useBobcatFlag('fiveStageNav') === 'allowed';
  const command = tableStore.cellValueForEditor(rowId, NodeColumns.command);
  const isMultiMatchRouting =
    manageIntentsRoutingEnabled && command === 'SysMultiMatchRouting';

  const { data } = parseParameterInput(raw);

  // Not a JSON object — the plain textarea IS the right editor for this value
  // (a bare array/scalar, or invalid JSON mid-edit), same as before this
  // field existed. No drill-down to offer.
  if (data === null) {
    return (
      <ExpandableTextarea
        labelText="Parameter Input"
        className="c-node-property__textarea c-param-input__scrollable"
        testId="ps-node-property-textarea-parameterInput"
        value={raw}
        disabled={disabled}
        onFocus={commit.onFocus}
        onChange={commit.onChange}
        onBlur={commit.onBlur}
      />
    );
  }

  if (isMultiMatchRouting && activeSection !== 'raw') {
    return (
      <div className="c-param-input" test-id="ps-param-input-section-intents">
        <RoutingIntentsField
          rowId={rowId}
          parameterInputRaw={raw}
          disabled={disabled}
          onShowRawJson={() => setActiveSection('raw')}
        />
      </div>
    );
  }

  const present = detectSections(data);
  const leftover = otherKeys(data);
  const sections = PARAMETER_INPUT_SECTIONS.filter((s) =>
    present.includes(s.id)
  );

  const writePath = (path: FieldPath, value: unknown) => {
    const next = setParameterInputPath(data, path, value);
    commit.onChange(serializeParameterInput(next));
  };

  if (activeSection === null) {
    return (
      <div className="c-param-input" test-id="ps-param-input-list">
        <ul className="c-param-input__list">
          {sections.map((section) => (
            <li key={section.id}>
              <button
                type="button"
                className="c-param-input__row"
                test-id={`ps-param-input-row-${section.id}`}
                onClick={() => setActiveSection(section.id)}
              >
                <Icon name={SECTION_ICON[section.id]} size="xsmall" />
                <span className="c-param-input__row-label">
                  {section.label}
                </span>
                <span className="c-param-input__row-summary">
                  {summarizeSection(data, section.id)}
                </span>
                <Icon name="chevron-right" size="xsmall" />
              </button>
            </li>
          ))}
          {leftover.length > 0 && (
            <li>
              <button
                type="button"
                className="c-param-input__row"
                test-id="ps-param-input-row-other"
                onClick={() => setActiveSection('other')}
              >
                <Icon name={SECTION_ICON.other} size="xsmall" />
                <span className="c-param-input__row-label">Other</span>
                <span className="c-param-input__row-summary">
                  {summarizeSection(data, 'other')}
                </span>
                <Icon name="chevron-right" size="xsmall" />
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              className="c-param-input__row"
              test-id="ps-param-input-row-raw"
              onClick={() => setActiveSection('raw')}
            >
              <Icon name="data-script" size="xsmall" />
              <span className="c-param-input__row-label">Raw JSON</span>
              <span className="c-param-input__row-summary">
                Edit the whole value directly
              </span>
              <Icon name="chevron-right" size="xsmall" />
            </button>
          </li>
        </ul>
      </div>
    );
  }

  const backButton = (label: string) => (
    <button
      type="button"
      className="c-param-input__back"
      test-id="ps-param-input-back"
      onClick={() => setActiveSection(null)}
    >
      <Icon name="chevron-left" size="xsmall" />
      {label}
    </button>
  );

  if (activeSection === 'raw') {
    return (
      <div className="c-param-input" test-id="ps-param-input-section-raw">
        {backButton(
          isMultiMatchRouting ? 'Back to intents' : 'Back to sections'
        )}
        <ExpandableTextarea
          labelText="Parameter Input (raw JSON)"
          className="c-node-property__textarea c-param-input__raw c-param-input__scrollable"
          testId="ps-node-property-textarea-parameterInput"
          value={raw}
          disabled={disabled}
          onFocus={commit.onFocus}
          onChange={commit.onChange}
          onBlur={commit.onBlur}
        />
      </div>
    );
  }

  if (activeSection === 'other') {
    const otherData = Object.fromEntries(
      leftover.map((key) => [key, data[key]])
    );
    return (
      <div className="c-param-input" test-id="ps-param-input-section-other">
        {backButton('Back to sections')}
        <ExpandableTextarea
          label={<label className="c-node-property__label">Other fields</label>}
          labelText="Other fields"
          className="c-node-property__textarea c-param-input__scrollable"
          testId="ps-param-input-other-json"
          value={JSON.stringify(otherData, null, 2)}
          disabled={disabled}
          onFocus={commit.onFocus}
          onChange={(next) => {
            try {
              const parsed = JSON.parse(next);
              if (
                parsed &&
                typeof parsed === 'object' &&
                !Array.isArray(parsed)
              ) {
                commit.onChange(
                  serializeParameterInput(
                    mergeOtherKeys(data, leftover, parsed)
                  )
                );
              }
            } catch {
              // Not valid JSON yet (mid-edit) — hold off writing back until
              // it parses again, same tolerance the raw textarea gives.
            }
          }}
          onBlur={commit.onBlur}
        />
      </div>
    );
  }

  return (
    <div
      className="c-param-input"
      test-id={`ps-param-input-section-${activeSection}`}
    >
      {backButton('Back to sections')}
      <SectionFields
        sectionId={activeSection}
        data={data}
        disabled={disabled}
        commit={commit}
        writePath={writePath}
      />
    </div>
  );
};

type WritePath = (path: FieldPath, value: unknown) => void;

interface FieldParams {
  label: string;
  path: FieldPath;
  data: Record<string, unknown>;
  disabled: boolean;
  commit: Commit;
  writePath: WritePath;
}

const textField = (
  { label, path, data, disabled, commit, writePath }: FieldParams,
  opts?: { readOnly?: boolean; help?: string }
) => {
  const value = getParameterInputPath(data, path);
  const text = value == null ? '' : String(value);
  const isReadOnly = disabled || Boolean(opts?.readOnly);
  return (
    <div className="c-node-property" key={path.join('.')}>
      <label className="c-node-property__label">{label}</label>
      <input
        type="text"
        className="c-node-property__input"
        test-id={`ps-param-input-field-${path.join('-')}`}
        value={text}
        disabled={isReadOnly}
        onFocus={commit.onFocus}
        onChange={(e) => writePath(path, e.currentTarget.value)}
        onBlur={commit.onBlur}
      />
      {opts?.help && <div className="c-node-property__help">{opts.help}</div>}
    </div>
  );
};

/** Same as `textField`, but writes back a NUMBER (or clears the key on an
 *  empty input) instead of a string — for fields whose real production
 *  value is numeric (`parameters.max_output_tokens`, `mcp.max_iterations`),
 *  so editing them here can't silently flip `2048` into `"2048"` in the
 *  stored JSON. */
const numberField = ({
  label,
  path,
  data,
  disabled,
  commit,
  writePath,
}: FieldParams) => {
  const value = getParameterInputPath(data, path);
  const text = value == null ? '' : String(value);
  return (
    <div className="c-node-property" key={path.join('.')}>
      <label className="c-node-property__label">{label}</label>
      <input
        type="number"
        className="c-node-property__input"
        test-id={`ps-param-input-field-${path.join('-')}`}
        value={text}
        disabled={disabled}
        onFocus={commit.onFocus}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          writePath(path, raw === '' ? undefined : Number(raw));
        }}
        onBlur={commit.onBlur}
      />
    </div>
  );
};

const textareaField = ({
  label,
  path,
  data,
  disabled,
  commit,
  writePath,
}: FieldParams) => {
  const value = getParameterInputPath(data, path);
  const text = value == null ? '' : String(value);
  return (
    <div className="c-node-property" key={path.join('.')}>
      <ExpandableTextarea
        label={<label className="c-node-property__label">{label}</label>}
        labelText={label}
        className="c-node-property__textarea c-param-input__scrollable"
        testId={`ps-param-input-field-${path.join('-')}`}
        value={text}
        disabled={disabled}
        onFocus={commit.onFocus}
        onChange={(next) => writePath(path, next)}
        onBlur={commit.onBlur}
      />
    </div>
  );
};

const jsonSubField = ({
  label,
  path,
  data,
  disabled,
  commit,
  writePath,
}: FieldParams) => {
  const value = getParameterInputPath(data, path);
  const text = value === undefined ? '' : JSON.stringify(value, null, 2);
  return (
    <div className="c-node-property" key={path.join('.')}>
      <ExpandableTextarea
        label={<label className="c-node-property__label">{label}</label>}
        labelText={label}
        className="c-node-property__textarea c-param-input__scrollable"
        testId={`ps-param-input-field-${path.join('-')}`}
        value={text}
        disabled={disabled}
        onFocus={commit.onFocus}
        onChange={(raw) => {
          if (!raw.trim()) {
            writePath(path, undefined);
            return;
          }
          try {
            writePath(path, JSON.parse(raw));
          } catch {
            // Mid-edit / not valid JSON yet — don't write back until it
            // parses again, matching every other JSON escape hatch here.
          }
        }}
        onBlur={commit.onBlur}
      >
        <div className="c-node-property__help">Edited as JSON.</div>
      </ExpandableTextarea>
    </div>
  );
};

/** `live_corpus_id` is backend-managed (filled in once the solution
 *  publishes) — read-only wherever it appears, in whichever section claims
 *  it, rather than a special case the generic renderer has to know about
 *  by section id. */
const READ_ONLY_KEYS: Record<string, string> = {
  live_corpus_id:
    'Provisioned by the backend once the solution publishes — not editable here.',
};

/**
 * Renders one field per path `fieldPathsForSection` finds for this section —
 * generic over whichever of the section's claimed keys `data` actually has,
 * typed off each value's own shape (`inferFieldKind`). See
 * `parameter-input-sections.ts`'s module doc comment for why this is
 * schema-agnostic rather than a fixed field list: the same node's Parameter
 * Input can legitimately be either of (at least) two different real shapes.
 */
const SectionFields: React.FC<{
  sectionId: ParameterInputSectionId;
  data: Record<string, unknown>;
  disabled: boolean;
  commit: Commit;
  writePath: WritePath;
}> = ({ sectionId, data, disabled, commit, writePath }) => {
  const paths = fieldPathsForSection(data, sectionId);

  if (paths.length === 0) {
    return (
      <p className="c-node-property__help">Nothing set for this section yet.</p>
    );
  }

  return (
    <>
      {paths.map((path) => {
        const value = getParameterInputPath(data, path);
        const label = labelForKey(path[path.length - 1]);
        const readOnlyHelp = READ_ONLY_KEYS[path[path.length - 1]];
        const base: FieldParams = {
          label,
          path,
          data,
          disabled,
          commit,
          writePath,
        };

        switch (inferFieldKind(value)) {
          case 'number':
            return numberField(base);
          case 'textarea':
            return textareaField(base);
          case 'json':
            return jsonSubField(base);
          case 'text':
          default:
            return readOnlyHelp
              ? textField(base, { readOnly: true, help: readOnlyHelp })
              : textField(base);
        }
      })}
    </>
  );
};
